import type {
  OperationProgressMetric,
  OperationProgressModel,
  OperationProgressStep,
} from '../../../../shared/ui/progress';
import type { MappingProfileApplyProgressState } from '../../hooks/profileControllerShared';
import {
  childRepairStepIdMap,
  profileApplyStepPlan,
  type ProfileApplyStepId,
} from './stepPlan';

export const EtaPendingLabel = 'Waiting for Revit item counts and ETA';
const EtaPendingMetric = 'Pending Revit item counts';

function getMetricValue(metrics: OperationProgressMetric[] | undefined, label: string): string | null {
  return metrics?.find((metric) => metric.label === label)?.value ?? null;
}

function parseMissingCount(readinessMessage: string | null): string | null {
  if (!readinessMessage) {
    return null;
  }

  const match = readinessMessage.match(/Adding\s+(\d+)\s+missing/i);
  return match?.[1] ?? null;
}

function formatAreasFromChildSteps(childSteps: OperationProgressStep[]): string | null {
  const labels = childSteps
    .filter((step) => step.id !== 'verify' && step.id !== 'verifyProject')
    .map((step) => childRepairStepIdMap[step.id])
    .filter((id): id is ProfileApplyStepId => Boolean(id))
    .filter((id, index, values) => values.indexOf(id) === index)
    .map((id) => profileApplyStepPlan.find((step) => step.id === id)?.label.replace(' repair', '') ?? null)
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join(', ') : null;
}

export function hasLiveRevitCounts(model: OperationProgressModel | null): boolean {
  return Boolean(
    model &&
      (typeof model.processed === 'number' ||
        typeof model.total === 'number' ||
        typeof model.etaMs === 'number')
  );
}

export function getCurrentStepIndex(steps: OperationProgressStep[]): number {
  const activeIndex = steps.findIndex(
    (step) => step.status === 'current' || step.status === 'error'
  );

  return activeIndex >= 0 ? activeIndex : Math.max(0, steps.length - 1);
}

export function capRunningStagePercent(
  status: OperationProgressModel['status'],
  percent: number | null
): number | null {
  // A still-running workflow must never read as done. Error models keep the
  // raw value so the stabilizer can null a bare 100 that has no item evidence.
  if (percent === null || status !== 'running') {
    return percent;
  }

  return Math.min(99, percent);
}

export function getResolvedStepCount(steps: OperationProgressStep[]): number {
  return steps.filter((step) => step.status === 'complete' || step.status === 'skipped').length;
}

export function buildStagePercent(steps: OperationProgressStep[], childModel: OperationProgressModel | null): number | null {  if (steps.length === 0) {
    return null;
  }

  if (childModel && typeof childModel.percent === 'number') {
    // The child percent covers its whole repair span (repair steps + verify),
    // not only the active outer step. Folding it into the active step index
    // double-counted finished steps and pinned the bar near 100% during the
    // verify pass.
    const childFraction = Math.max(0, Math.min(100, childModel.percent)) / 100;
    const childSpanStepIds = new Set(
      (childModel.steps ?? [])
        .map((step) => childRepairStepIdMap[step.id])
        .filter((id): id is ProfileApplyStepId => Boolean(id))
    );
    const childSpanCount = Math.max(1, childSpanStepIds.size);
    const resolvedOutsideChildSpan = steps.filter(
      (step) =>
        (step.status === 'complete' || step.status === 'skipped') &&
        !childSpanStepIds.has(step.id as ProfileApplyStepId)
    ).length;

    return Math.round(((resolvedOutsideChildSpan + childFraction * childSpanCount) / steps.length) * 100);
  }

  const resolvedCount = getResolvedStepCount(steps);
  const currentIndex = steps.findIndex((step) => step.status === 'current');
  const currentStageFraction = currentIndex >= 0 ? 0.35 : 0;

  return Math.round(((resolvedCount + currentStageFraction) / steps.length) * 100);
}

export function buildProfileApplyMetrics(input: {
  steps: OperationProgressStep[];
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  readinessMessage: string | null;
  profileSessionPending: boolean;
}): OperationProgressMetric[] {
  const childMetrics = input.readinessProgressModel?.metrics ?? [];
  const liveCounts = hasLiveRevitCounts(input.readinessProgressModel);
  const missingCount =
    getMetricValue(childMetrics, 'Missing') ??
    parseMissingCount(input.readinessMessage) ??
    (input.readinessStatus === 'ready' && !input.profileSessionPending ? '0' : 'Pending');
  const areas =
    getMetricValue(childMetrics, 'Areas') ??
    formatAreasFromChildSteps(input.readinessProgressModel?.steps ?? []) ??
    (input.readinessStatus === 'ready' && !input.profileSessionPending
      ? 'No setup changes required'
      : 'Planning setup changes');
  const metrics: OperationProgressMetric[] = [
    {
      label: 'Workflow phase',
      value: `${getResolvedStepCount(input.steps)} / ${input.steps.length}`,
      tone: 'info',
    },
    {
      label: 'Missing parameters',
      value: missingCount,
      tone: missingCount === '0' ? 'success' : missingCount === 'Pending' ? 'default' : 'warning',
    },
    {
      label: 'Setup areas',
      value: areas,
      tone: areas === 'No setup changes required' ? 'success' : 'info',
    },
    {
      label: 'Revit ETA',
      value: liveCounts ? 'Calculated from Revit progress' : EtaPendingMetric,
      tone: liveCounts ? 'info' : 'default',
    },
  ];
  const ownedMetricLabels = new Set(metrics.map((metric) => metric.label));

  return [
    ...metrics,
    ...childMetrics.filter((metric) => !ownedMetricLabels.has(metric.label)),
  ];
}

export function buildProfileApplyMessage(input: {
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  readinessMessage: string | null;
  profileSessionPending: boolean;
}): string {
  const { profileApplyProgress, readinessProgressModel, readinessStatus, readinessMessage } = input;
  const childHasActiveStep = readinessProgressModel?.steps?.some(
    (step) => step.status === 'current' || step.status === 'error'
  ) ?? false;

  if (
    readinessProgressModel?.message &&
    (readinessProgressModel.status !== 'complete' || childHasActiveStep)
  ) {
    return readinessProgressModel.message;
  }

  if (profileApplyProgress?.phase === 'error') {
    return profileApplyProgress.message || 'PipeEX could not load the mapping profile; refresh profiles, then try again';
  }

  if (profileApplyProgress && profileApplyProgress.phase !== 'complete') {
    return profileApplyProgress.message;
  }

  if (readinessStatus === 'repairing') {
    return readinessMessage || 'Planning the required Revit parameter setup';
  }

  if (readinessStatus === 'blocked' || readinessStatus === 'error') {
    return readinessMessage || 'PipeEX could not complete Revit parameter setup; review the failed phase, then retry';
  }

  if (readinessStatus === 'ready' && !input.profileSessionPending) {
    return readinessMessage || 'The mapping profile matches the active Revit project and is ready for PCF Validation';
  }

  return readinessMessage || 'Preparing to compare profile parameters with the active Revit project';
}

export function buildProfileApplyDetail(input: {
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
}): string | undefined {
  if (input.profileApplyProgress?.phase === 'error') {
    return input.profileApplyProgress.error || input.profileApplyProgress.detail;
  }

  if (input.profileApplyProgress && input.profileApplyProgress.phase !== 'complete') {
    return input.profileApplyProgress.error || input.profileApplyProgress.detail;
  }

  const childCompletionIsStaleFailureContext =
    (input.readinessStatus === 'blocked' || input.readinessStatus === 'error') &&
    input.readinessProgressModel?.status === 'complete';

  if (input.readinessProgressModel?.detail && !childCompletionIsStaleFailureContext) {
    return input.readinessProgressModel.detail;
  }

  if (input.readinessProgressModel?.currentItem) {
    return input.readinessProgressModel.currentItem;
  }

  if (input.readinessStatus === 'blocked' || input.readinessStatus === 'error') {
    return 'A project or family parameter could not be prepared. Review the failed item and binding in Revit, then retry the project check.';
  }

  if (input.readinessStatus === 'repairing') {
    return 'PipeEX is classifying missing project and family parameters before Revit item counts become available.';
  }

  return 'PipeEX is reading active Revit project parameters to determine which setup changes are required.';
}

export function buildProfileApplyStatus(input: {
  profileApplyProgress: MappingProfileApplyProgressState | null;
  readinessProgressModel: OperationProgressModel | null;
  readinessStatus: string;
  profileSessionPending: boolean;
}): OperationProgressModel['status'] {
  if (input.profileApplyProgress?.phase === 'error') {
    return 'error';
  }

  if (input.readinessProgressModel?.status === 'error') {
    return 'error';
  }

  if (input.readinessStatus === 'blocked' || input.readinessStatus === 'error') {
    return 'error';
  }

  if (input.readinessProgressModel?.status === 'pending') {
    return 'pending';
  }

  if (
    input.readinessProgressModel?.status === 'waiting'
  ) {
    return 'waiting';
  }

  if (
    input.profileApplyProgress?.phase === 'complete' &&
    input.readinessStatus === 'ready' &&
    !input.profileSessionPending
  ) {
    return 'complete';
  }

  return 'running';
}
